// The readings of use behind the record's product sections (pipeline RC2, track B; brief of 7 Oct section 4a and 4b),
// as GET /api/records/companies/:id/usage returns them (track A's route, track B's CompanyReadings unchanged). Written out
// here because the portal does not import the API's code: apps/api/src/snowflake/readings-read.ts is the source, field for
// field. The derivations below are pure: what the OneTrade section prints and draws, never a figure the read lacks.

export type OneTradeLabel = 'up' | 'level' | 'down' | 'fell_with_season' | 'decline_raised' | 'tournament' | 'no_reading';
export type OneVideoLabel = 'up' | 'level' | 'down' | 'fell_with_season' | 'decline_raised' | 'no_reading';
export type InFrontLabel = 'on_integration' | 'calls_falling' | 'no_calls';

export interface OneTradeReadingMonth {
  month: string; label: OneTradeLabel; hours: number; expected: number | null; reading: number | null;
  windowReading: number | null; windowMonths: number; changeVsPrev3: number | null; hoursYearBefore: number | null;
  tournament: { league: string; hours: number; share: number } | null; raisedSince: string | null; belowSince: string | null;
  users: number | null; usersYearBefore: number | null; headlineHours: number | null; headlineHoursYearBefore: number | null;
  /** Coming from track B after RC7: the group's seats and the fixtures on offer across its leagues. */
  seats?: number | null; fixtures?: number | null;
}
export interface OneTradeLeague {
  league: string; sport: string; hours: number; hoursYearBefore: number; expected: number; fixtures: number;
  fixturesYearBefore: number; fixturesThreeMonthsBefore: number; basis: 'scaled' | 'unscaled' | 'season_over' | 'new' | 'unmatched';
}
/** A league or a sport over the last twelve months (track B, after RC7): its hours against what its own months lead us to expect. */
export interface OneTradeTotal { key: string; sport: string; hours: number; hoursYearBefore: number; expected: number; fixtures: number; reading: number | null }
export interface OneTradeGroupReading {
  groupId: number; months: OneTradeReadingMonth[]; leagues: OneTradeLeague[]; leagueMonths: Array<{ league: string; month: string; hours: number }>;
  /** The ten leagues and ten sports with the most hours over twelve months; absent from a build before RC7. */
  leagues12?: OneTradeTotal[]; sports12?: OneTradeTotal[];
}
export interface OneVideoAgreementReading {
  operatorId: number; rightsholder: string; competition: string; sport: string | null;
  months: Array<{ month: string; booked: number; taken: number; takeup: number | null; usual: number | null; points: number | null; label: OneVideoLabel }>;
}
export interface InFrontReading {
  operatorId: number; asOf: string; label: InFrontLabel; baselineFixtures: number; fixturesCalled: number; ratio: number | null;
  fixturesCalledSinceJuly: number; feedSetUp: boolean; stoppedBeforeJuly: boolean; viewsJanJun: number;
}
/** OneVideo's top ten leagues for one operator over twelve months (track B, RC11; U372: all leagues together, by events). */
export interface OneVideoLeague {
  league: string; sport: string; rightsholders: string[]; events: number; eventsYearBefore: number;
  views: number; viewsYearBefore: number;
  /** Set where every event of the twelve months came on InFront's own feed, which iGame served to 30 June 2026. */
  endedOn: string | null;
  months: Array<{ month: string; events: number }>;
}
export interface OneVideoClientLeagues { operatorId: number; through: string; events: number; leagues: OneVideoLeague[] }
export interface CompanyReadings {
  builtAt: string | null; ruleVersion: string | null; band: number | null; holdMonths: number | null;
  oneTrade: OneTradeGroupReading[]; oneVideo: OneVideoAgreementReading[]; inFront: InFrontReading[];
  /** Absent from a build before RC11. */
  oneVideoLeagues?: OneVideoClientLeagues[];
}

const LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const parts = (m: string): [number, number] => { const [y, mm] = m.split('-').map(Number); return [y ?? 0, (mm ?? 1) - 1]; };
/** "September 2026". */
export const monthLong = (m: string): string => { const [y, i] = parts(m); return `${LONG[i]} ${y}`; };
/** "Sep 2025", for a chart's two ends. */
export const monthShort = (m: string): string => { const [y, i] = parts(m); return `${SHORT[i]} ${y}`; };
/** "September", for a month named beside its own year a year before. */
export const monthName = (m: string): string => LONG[parts(m)[1]] ?? m;

const whole = (n: number): string => Math.round(n).toLocaleString('en-GB');
/** "20% below", "5% above", or "level" inside the band (10% unless the read says otherwise). */
export function againstWords(reading: number | null, band = 0.1): string | null {
  if (reading === null || !Number.isFinite(reading)) return null;
  if (Math.abs(reading) < band) return 'level';
  const p = Math.round(Math.abs(reading) * 100);
  return `${p}% ${reading < 0 ? 'below' : 'above'}`;
}

export interface GroupTotals {
  last: OneTradeReadingMonth;
  /** The last month against its own month a year before, in words, or null where there is no year before. */
  monthWords: string | null;
  /** The last twelve months against the twelve before, over the months that have both. */
  twelveWords: string | null;
}

/** The two comparisons the section prints under its charts (brief 4a). */
export function groupTotals(g: OneTradeGroupReading): GroupTotals | null {
  const last = g.months[g.months.length - 1];
  if (!last) return null;
  // Raw hours, before the games are allowed for: said as fewer or more hours, never "below" or "above", which the
  // reading against its own pattern says beside it (live, 7 Oct: Bet365's September was 23% fewer hours, 20% below its pattern).
  const change = (now: number, before: number): string => {
    const r = now / before - 1;
    const p = Math.round(Math.abs(r) * 100);
    return p === 0 ? 'the same' : `${p}% ${r < 0 ? 'fewer' : 'more'} hours`;
  };
  const monthWords = last.hoursYearBefore !== null && last.hoursYearBefore > 0
    ? `${monthName(last.month)}: ${whole(last.hours)} hours, against ${whole(last.hoursYearBefore)} in ${monthName(last.month)} ${parts(last.month)[0] - 1}, ${change(last.hours, last.hoursYearBefore)}`
    : null;
  const both = g.months.slice(-12).filter((m) => m.hoursYearBefore !== null && m.hoursYearBefore > 0);
  const now = both.reduce((a, m) => a + m.hours, 0);
  const before = both.reduce((a, m) => a + (m.hoursYearBefore ?? 0), 0);
  const twelveWords = both.length > 0 && before > 0
    ? `${both.length === 12 ? 'Twelve months' : `The ${both.length} months with a year before`}: ${whole(now)} hours, against ${whole(before)} the year before, ${change(now, before)}`
    : null;
  return { last, monthWords, twelveWords };
}

export interface TopLeague { league: string; name: string; sport: string; hours: number; fixtures: number; reading: string | null; spark: number[] }

/** The ten leagues it watched most in the last month, each with its reading against its own pattern and twelve months' hours. */
export function topLeagues(g: OneTradeGroupReading, band = 0.1, n = 10): TopLeague[] {
  const months = [...new Set(g.leagueMonths.map((x) => x.month))].sort().slice(-12);
  return [...g.leagues].filter((l) => l.hours > 0).sort((a, b) => b.hours - a.hours || a.league.localeCompare(b.league)).slice(0, n).map((l) => ({
    league: l.league,
    name: l.league.includes('|') ? l.league.slice(l.league.indexOf('|') + 1) : l.league,
    sport: l.sport,
    hours: l.hours,
    fixtures: l.fixtures,
    reading: l.expected > 0 ? againstWords(l.hours / l.expected - 1, band) : l.basis === 'new' ? 'new this year' : null,
    spark: months.map((m) => g.leagueMonths.find((x) => x.league === l.league && x.month === m)?.hours ?? 0),
  }));
}

const pct = (x: number): string => `${Math.round(x * 100)}%`;

/** A OneVideo month's reading in words: the share it took against its usual, in percentage points (RC2's points). */
export function takeupWords(label: OneVideoLabel, points: number | null): string {
  const p = points === null ? null : Math.round(Math.abs(points));
  switch (label) {
    case 'up': return p ? `up ${p} points` : 'up';
    case 'down': return p ? `down ${p} points` : 'down';
    case 'level': return 'level';
    case 'fell_with_season': return 'fell with the season';
    case 'decline_raised': return 'decline raised';
    case 'no_reading': return 'no reading yet';
  }
}

/** The twelve months to a month, oldest first, as YYYY-MM-01. */
export function lastTwelve(month: string): string[] {
  const [y, i] = parts(month);
  return Array.from({ length: 12 }, (_, k) => { const d = new Date(Date.UTC(y, i - 11 + k, 1)); return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`; });
}

/** The last complete month before a day: "2026-10-07" reads September 2026. */
export function lastCompleteMonth(day: string): string {
  const [y, i] = parts(day);
  const d = new Date(Date.UTC(y, i - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

export interface AgreementRow {
  key: string; competition: string; rightsholder: string; sport: string | null;
  /** The last month read across all its agreements, and this one's taken of booked in it, or why there is none. */
  last: string; lastWords: string; usualWords: string | null; reading: string | null;
  totalWords: string | null; spark: number[];
}

/** OneVideo read per agreement (brief 4a): the share of the events booked for it that it took, against its own usual. Never the whole basket. */
export function agreementRows(agreements: readonly OneVideoAgreementReading[], month: string | null = null): AgreementRow[] {
  const months = [...new Set(agreements.flatMap((a) => a.months.map((m) => m.month)))].sort();
  // The month the section reads (the read's last complete month), so the table never reads an older month than the
  // sentence above it (live, 7 Oct: Betsson's said nothing was booked in September over a table reading May).
  const last = month ?? months[months.length - 1];
  if (!last || months.length === 0) return [];
  const shown = lastTwelve(last);
  const bookedLast = (a: OneVideoAgreementReading) => a.months.find((m) => m.month === last)?.booked ?? 0;
  const total = (a: OneVideoAgreementReading) => a.months.reduce((s, m) => s + m.booked, 0);
  return [...agreements].sort((a, b) => bookedLast(b) - bookedLast(a) || total(b) - total(a) || a.competition.localeCompare(b.competition)).map((a) => {
    const m = a.months.find((x) => x.month === last && x.booked > 0);
    const inShown = a.months.filter((x) => shown.includes(x.month));
    const booked = inShown.reduce((s, x) => s + x.booked, 0);
    const taken = inShown.reduce((s, x) => s + x.taken, 0);
    return {
      key: `${a.operatorId}|${a.rightsholder}|${a.competition}`, competition: a.competition, rightsholder: a.rightsholder, sport: a.sport, last,
      lastWords: m ? `${whole(m.taken)} of ${whole(m.booked)}${m.takeup !== null ? `, ${pct(m.takeup)}` : ''}` : `none booked in ${monthName(last)}`,
      usualWords: m && m.usual !== null ? pct(m.usual) : null,
      reading: m ? takeupWords(m.label, m.points) : null,
      totalWords: booked > 0 ? `${whole(taken)} of ${whole(booked)}, ${pct(taken / booked)}` : null,
      spark: shown.map((mo) => a.months.find((x) => x.month === mo)?.takeup ?? 0),
    };
  });
}

export interface InFrontWords { since: string; sinceNote: string; before: string }

/** InFront's streams after 1 July (brief 4b): the "since" box of the three, in the reading's words. Gone direct is a person's note, not yet stored. */
export function inFrontWords(r: InFrontReading, name: string): InFrontWords {
  const called = `${whole(r.fixturesCalled)} InFront ${r.fixturesCalled === 1 ? 'fixture' : 'fixtures'} called in ${monthName(r.asOf)}, against ${whole(r.baselineFixtures)} a month from January to May`;
  const before = `${whole(r.viewsJanJun)} views from January to June${r.baselineFixtures > 0 ? `; about ${whole(r.baselineFixtures)} InFront fixtures a month from January to May` : ''}${r.stoppedBeforeJuly ? `. ${name} had stopped calling for them before July` : ''}.`;
  switch (r.label) {
    case 'on_integration':
      return { since: 'On our integration', sinceNote: `${called}.`, before };
    case 'calls_falling': {
      const fall = r.ratio === null ? null : Math.round((1 - r.ratio) * 100);
      return { since: fall !== null && fall > 0 ? `Calls down ${fall}%` : 'Calls falling', sinceNote: `${called}; ${whole(r.fixturesCalledSinceJuly)} since 1 July.`, before };
    }
    case 'no_calls':
      return { since: `No calls from ${name}`, sinceNote: `No calls since 1 July${r.feedSetUp ? ', though a feed is set up for it' : ', and no feed set up'}.`, before };
  }
}

export interface TopTotal { key: string; name: string; sport: string | null; hours: number; fixtures: number; reading: string | null; spark: number[] | null }

/** The top tens over twelve months (brief 4a): each with its hours, its games and its reading against its own pattern; a league with twelve months drawn. */
export function topTotals(g: OneTradeGroupReading, what: 'leagues' | 'sports', band = 0.1): TopTotal[] {
  const list = (what === 'leagues' ? g.leagues12 : g.sports12) ?? [];
  const months = [...new Set(g.leagueMonths.map((x) => x.month))].sort().slice(-12);
  return list.map((t) => ({
    key: t.key,
    name: what === 'leagues' && t.key.includes('|') ? t.key.slice(t.key.indexOf('|') + 1) : t.key,
    sport: what === 'leagues' ? t.sport : null,
    hours: t.hours,
    fixtures: t.fixtures,
    reading: t.reading !== null ? againstWords(t.reading, band) : t.hoursYearBefore === 0 && t.hours > 0 ? 'new this year' : null,
    spark: what === 'leagues' && months.length ? months.map((m) => g.leagueMonths.find((x) => x.league === t.key && x.month === m)?.hours ?? 0) : null,
  }));
}

export interface LeagueRow { key: string; league: string; sport: string; holders: string; events: string; share: string | null; sinceJuly: string; against: string; views: string; ended: string | null; spark: number[] }

const JULY = '2026-07-01';
/** "up 66%", "down 23%", "new" where it had none the year before, "the same". */
export function againstYear(now: number, before: number): string {
  if (before <= 0) return now > 0 ? 'new' : 'none either year';
  const p = Math.round((now / before - 1) * 100);
  return p === 0 ? 'the same' : `${p > 0 ? 'up' : 'down'} ${Math.abs(p)}%`;
}

/** OneVideo's top ten leagues as the record lists them (brief 4a, drawing a6-account-file.html "Top 10 leagues"). */
export function leagueRows(c: OneVideoClientLeagues): LeagueRow[] {
  return c.leagues.map((l) => {
    const since = l.months.filter((m) => m.month >= JULY).reduce((a, m) => a + m.events, 0);
    const ended = l.endedOn ? `InFront's, ended ${day(l.endedOn)}` : null;
    return {
      key: l.league, league: l.league, sport: l.sport, holders: l.rightsholders.join(', '),
      events: whole(l.events), share: c.events > 0 ? `${(Math.round((l.events / c.events) * 1000) / 10).toLocaleString('en-GB')}% of its events` : null,
      sinceJuly: since > 0 ? whole(since) : 'none', against: againstYear(l.events, l.eventsYearBefore),
      views: l.views > 0 ? whole(l.views) : 'none', ended, spark: l.months.map((m) => m.events),
    };
  });
}
const day = (d: string): string => { const [y, i] = parts(d); return `${Number(d.slice(8, 10))} ${SHORT[i]} ${y}`; };
